// JanetFAB — the floating Janet button + panel shell. The conversation itself
// is JanetChatPanel (shared with /janet/chat, the desktop Janet window and the
// deal Workroom's docked inspector). This file only owns: the round button,
// the floating container, click-outside close, and which topic is active.
import { useEffect, useRef } from 'react'
import { Bot, X } from 'lucide-react'
import { useJanetPage } from '../../contexts/JanetPageContext'
import JanetChatPanel from './JanetChatPanel'
import { useJanetUnread, useMarkVisibleTopicRead } from '../../hooks/useJanetUnread'

export default function JanetFAB({ bottomOffset = 'bottom-6' }) {
  const { page, isOpen, openJanet, closeJanet, activeSessionId, pickTopic, newTopic, pttPending, clearPttPending } = useJanetPage()
  const wrapRef = useRef(null)
  // Conversations Janet started and you haven't opened: a count on the button,
  // and opening Janet lands on the newest of them.
  const unread = useJanetUnread()
  useMarkVisibleTopicRead(activeSessionId, isOpen)

  // Click outside the panel closes it (desktop); mobile is full-screen with its own X.
  useEffect(() => {
    if (!isOpen) return
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) closeJanet()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [isOpen, closeJanet])

  useEffect(() => { if (!isOpen && pttPending) clearPttPending() }, [isOpen, pttPending, clearPttPending])

  return (
    <div className={`fixed ${bottomOffset} right-6 z-40`} ref={wrapRef}>
      <button
        onClick={() => {
          if (isOpen) { closeJanet(); return }
          if (unread.latestId) pickTopic(unread.latestId)
          openJanet()
        }}
        className={`relative w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all duration-200 ${
          isOpen ? 'bg-warm-200 text-warm-700 rotate-90' : 'bg-janet-700 text-white hover:bg-janet-600'
        }`}
        title={unread.count ? `Janet started ${unread.count} conversation${unread.count === 1 ? '' : 's'}` : 'Ask Janet'}
      >
        {isOpen ? <X size={20} /> : <Bot size={20} />}
        {!isOpen && unread.count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold leading-[18px] text-center">{unread.count}</span>
        )}
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 w-full h-[100dvh] bg-white shadow-2xl flex flex-col overflow-hidden sm:absolute sm:inset-auto sm:bottom-14 sm:right-0 sm:z-auto sm:w-[26rem] sm:max-w-[calc(100vw-3rem)] sm:h-[min(600px,calc(100vh-11rem))] sm:rounded-2xl sm:border sm:border-warm-200">
          <JanetChatPanel
            layout="fab"
            sessionId={activeSessionId}
            page={page}
            onClose={closeJanet}
            onNewTopic={() => newTopic()}
            onPickTopic={pickTopic}
            startListeningOnMount={pttPending}
          />
        </div>
      )}
    </div>
  )
}
